import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import {
  AdminIcon,
  ManagementHeader,
  MetricCard,
  adminManagementStyles as styles,
  conciseId,
} from '../../../../components/admin/AdminManagement';
import { Alert } from '../../../../components/Alert';
import { billingApi } from '../../../../features/billing/billing.api';
import { operationalApi } from '../../../../features/reporting/operational.api';
import { toDisplayMessage } from '../../../../lib/error-message';
import { formatNgn } from '../../../../lib/format-currency';
import { formatBusinessDate } from '../../../../lib/format-date';
import { formatStatusLabel } from '../../../../lib/format-status';
import { paths, routeTo } from '../../../../routes/paths';

export function BillingDashboardPage(): React.JSX.Element {
  const aging = useQuery({
    queryKey: ['admin', 'billing', 'aging'],
    queryFn: () => operationalApi.repaymentAging(),
  });
  const overdue = useQuery({
    queryKey: ['admin', 'billing', 'overdue'],
    queryFn: () => billingApi.listOverdue(),
  });
  const repayments = useQuery({
    queryKey: ['admin', 'billing', 'repayments'],
    queryFn: () => billingApi.listAdminRepayments({ pageSize: 50 }),
  });
  const items = repayments.data?.items ?? [];
  return (
    <div className={styles.page}>
      <ManagementHeader
        eyebrow="Collections operations"
        title="Repayments"
        subtitle="Prioritize due and overdue instalments using Lagos-date aging."
        icon="calendar"
        context="Repayment schedules"
        action={
          <div className={styles.headerAction}>
            <Link to={paths.adminPaymentAttempts}>Payment attempts</Link>
            <span>·</span>
            <Link to={paths.adminPayments}>Posted payments</Link>
          </div>
        }
      />
      <section className={styles.hero}>
        <div>
          <h2>Collection priorities at a glance</h2>
          <p>Aging buckets are classified by the backend using Africa/Lagos calendar semantics.</p>
        </div>
        <strong>{aging.data ? `As of ${aging.data.asOfDate}` : 'Collections'}</strong>
      </section>
      <section className={styles.metrics}>
        <MetricCard
          label="Due today"
          value={aging.data?.dueToday.count ?? '—'}
          note={aging.data ? formatNgn(aging.data.dueToday.amount) : undefined}
          icon="calendar"
        />
        <MetricCard
          label="Upcoming this week"
          value={aging.data?.upcomingThisWeek.count ?? '—'}
          note={aging.data ? formatNgn(aging.data.upcomingThisWeek.amount) : undefined}
          icon="clock"
          tone="blue"
        />
        <MetricCard
          label="Overdue"
          value={aging.data?.overdue.count ?? '—'}
          note={aging.data ? formatNgn(aging.data.overdue.amount) : undefined}
          icon="warning"
          tone="amber"
        />
        <MetricCard
          label="Paid this month"
          value={aging.data?.paidThisMonth.count ?? '—'}
          note={aging.data ? formatNgn(aging.data.paidThisMonth.amount) : undefined}
          icon="check"
          tone="violet"
        />
      </section>
      <div className={styles.contentGrid}>
        <section className={styles.mainCard}>
          <header className={styles.sectionHeader}>
            <div className={styles.sectionTitle}>
              <span className={styles.sectionIcon}>
                <AdminIcon name="calendar" />
              </span>
              <div>
                <h2>Repayment schedule</h2>
                <p>Current instalments across active contracts</p>
              </div>
            </div>
            {repayments.data && (
              <span className={styles.countBadge}>
                {repayments.data.pagination.totalItems} schedules
              </span>
            )}
          </header>
          {repayments.isPending && <p className={styles.empty}>Loading repayment schedules…</p>}
          {repayments.isError && (
            <div className={styles.alertWrap}>
              <Alert tone="error">{toDisplayMessage(repayments.error)}</Alert>
            </div>
          )}
          {repayments.isSuccess && items.length === 0 && (
            <p className={styles.empty}>No repayment schedules are available yet.</p>
          )}
          {items.length > 0 && (
            <div className={styles.tableWrap}>
              <table className={styles.table} aria-label="Repayment schedules">
                <thead>
                  <tr>
                    <th>Instalment</th>
                    <th>Contract</th>
                    <th>Product</th>
                    <th>Due date</th>
                    <th>Amount</th>
                    <th>Paid</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((schedule) => (
                    <tr key={schedule.id}>
                      <td>#{schedule.sequenceNumber}</td>
                      <td>
                        <strong>
                          {schedule.contractReference ?? conciseId(schedule.subscriptionId)}
                        </strong>
                      </td>
                      <td>{schedule.productName ?? '—'}</td>
                      <td>{formatBusinessDate(schedule.dueDate)}</td>
                      <td>{formatNgn(schedule.instalmentAmount)}</td>
                      <td>{formatNgn(schedule.amountPaid)}</td>
                      <td>
                        <span
                          className={`${styles.status} ${schedule.status === 'PAID' ? styles.success : schedule.status === 'OVERDUE' ? styles.danger : styles.warning}`}
                        >
                          {formatStatusLabel(schedule.status)}
                        </span>
                      </td>
                      <td>
                        <Link to={routeTo.adminBillingRepayment(schedule.id)}>Open →</Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
        <aside className={styles.rail}>
          <section className={styles.railCard}>
            <h2>Overdue summary</h2>
            <p>Current collections follow-up from the billing service.</p>
            <ul className={styles.railList}>
              <li>
                <span>Overdue instalments</span>
                <strong>{overdue.data?.overdueCount ?? '—'}</strong>
              </li>
              <li>
                <span>Awaiting authorization</span>
                <strong>{overdue.data?.awaitingAuthorizationCount ?? '—'}</strong>
              </li>
              <li>
                <span>Total overdue</span>
                <strong>{overdue.data ? formatNgn(overdue.data.totalOverdueAmount) : '—'}</strong>
              </li>
            </ul>
          </section>
          <section className={styles.railCard}>
            <h2>Overdue aging</h2>
            <p>Mutually exclusive backend aging bands.</p>
            <ul className={styles.railList}>
              {aging.data && (
                <>
                  <li>
                    <span>1–7 days</span>
                    <strong>
                      {aging.data.overdueAging.days1to7.count} ·{' '}
                      {formatNgn(aging.data.overdueAging.days1to7.amount)}
                    </strong>
                  </li>
                  <li>
                    <span>8–30 days</span>
                    <strong>
                      {aging.data.overdueAging.days8to30.count} ·{' '}
                      {formatNgn(aging.data.overdueAging.days8to30.amount)}
                    </strong>
                  </li>
                  <li>
                    <span>31+ days</span>
                    <strong>
                      {aging.data.overdueAging.days31plus.count} ·{' '}
                      {formatNgn(aging.data.overdueAging.days31plus.amount)}
                    </strong>
                  </li>
                </>
              )}
            </ul>
          </section>
          <section className={styles.railCard}>
            <h2>Later schedules</h2>
            <p>After the current Lagos business week.</p>
            <ul className={styles.railList}>
              <li>
                <span>
                  <i className={styles.dot} />
                  Scheduled later
                </span>
                <strong>{aging.data?.later.count ?? '—'}</strong>
              </li>
              <li>
                <span>Amount</span>
                <strong>{aging.data ? formatNgn(aging.data.later.amount) : '—'}</strong>
              </li>
            </ul>
          </section>
        </aside>
      </div>
    </div>
  );
}
